import type { KycFileCertificationChoiceDto } from '@dealstack/shared/lib/generated';
import { uploadFileV2 } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	getProofOfAddress,
	getProofOfIdentity,
	updateProofOfAddress,
	updateProofOfIdentity,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useFileUploadErrorHandler } from '@dealstack/frontend/investor/hooks/useFileUploadErrorHandler';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	noSave,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { useEffect } from 'react';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';

interface Props {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	kycWorkflowId: string;
}

export const useInvestmentManagePersonalIDsController = ({
	setIsContinueDisabled,
	kycWorkflowId,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const handleUploadError = useFileUploadErrorHandler();

	const proofOfIdentityQuery = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_KYC_PROOF_OF_IDENTITY_GET, kycWorkflowId],
		queryFn: () => getProofOfIdentity(kycWorkflowId),
	});
	const uploadProofOfIdentityMutation = useMutation({
		mutationFn: uploadFileV2,
		onError: handleUploadError,
	});
	const updateProofOfIdentityMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateProofOfIdentity>[0]) =>
			updateProofOfIdentity(variables),
	});

	const onDropProofOfIdentity = (file: File, certification?: KycFileCertificationChoiceDto) => {
		uploadProofOfIdentityMutation.mutate(file, {
			onSuccess: ({ payload }) => {
				if (!payload) {
					return;
				}
				updateProofOfIdentityMutation.mutate(
					{ fileId: payload.id, kycWorkflowId, certification },
					{
						onSuccess: () => {
							showToastV2({ title: tInvestor('investment.toast.proof_uploaded') });
							void proofOfIdentityQuery.refetch();
						},
					}
				);
			},
		});
	};

	const onDeleteProofOfIdentity = () => {
		updateProofOfIdentityMutation.mutate(
			{ kycWorkflowId },
			{
				onSuccess: () => {
					showToastV2({ title: tInvestor('investment.toast.proof_removed') });
					void proofOfIdentityQuery.refetch();
				},
			}
		);
	};

	const proofOfAddressQuery = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_KYC_PROOF_OF_ADDRESS_GET, kycWorkflowId],
		queryFn: () => getProofOfAddress(kycWorkflowId),
	});
	const uploadProofOfAddressMutation = useMutation({
		mutationFn: uploadFileV2,
		onError: handleUploadError,
	});
	const updateProofOfAddressMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateProofOfAddress>[0]) =>
			updateProofOfAddress(variables),
	});

	const onDropProofOfAddress = (file: File, certification?: KycFileCertificationChoiceDto) => {
		uploadProofOfAddressMutation.mutate(file, {
			onSuccess: ({ payload }) => {
				if (!payload) {
					return;
				}
				updateProofOfAddressMutation.mutate(
					{ fileId: payload.id, kycWorkflowId, certification },
					{
						onSuccess: () => {
							showToastV2({ title: tInvestor('investment.toast.proof_uploaded') });
							void proofOfAddressQuery.refetch();
						},
					}
				);
			},
		});
	};

	const onDeleteProofOfAddress = () => {
		updateProofOfAddressMutation.mutate(
			{ kycWorkflowId },
			{
				onSuccess: () => {
					showToastV2({ title: tInvestor('investment.toast.proof_removed') });
					void proofOfAddressQuery.refetch();
				},
			}
		);
	};

	const proofOfIdentityDetails = proofOfIdentityQuery.data?.payload;
	const proofOfAddressDetails = proofOfAddressQuery.data?.payload;

	useEffect(() => {
		const isAllUploaded = proofOfIdentityDetails && proofOfAddressDetails;
		const isNotRejected =
			!isKycRejected(proofOfIdentityDetails?.status) &&
			!isKycRejected(proofOfAddressDetails?.status);

		setIsContinueDisabled(!isAllUploaded || !isNotRejected);
	}, [proofOfIdentityDetails, proofOfAddressDetails]);

	useWizardStep(noSave);

	return {
		uploadProofOfAddressRequest: uploadProofOfAddressMutation,
		uploadProofOfIdentityRequest: uploadProofOfIdentityMutation,
		isLoading: proofOfIdentityQuery.isLoading || proofOfAddressQuery.isLoading,
		proofOfIdentityDetails,
		proofOfAddressDetails,
		onDropProofOfIdentity,
		onDeleteProofOfIdentity,
		onDropProofOfAddress,
		onDeleteProofOfAddress,
		refetchAll: () => {
			void proofOfIdentityQuery.refetch();
			void proofOfAddressQuery.refetch();
		},
	};
};
